import React, { useEffect, useState, lazy } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { signs, getSignById } from '../../data/signs';
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8D48B';
/**
 * Mobile-only sticky CTA fixed at the bottom of the viewport.
 *
 * Behavior:
 *  - On the landing page (/): opens a modal with the 12 Reinos grid.
 *    Selecting a Reino navigates to its subpage.
 *  - On a sign subpage (/:signId): smooth-scrolls to the #sign-offers block.
 *  - Hidden on portal, legal pages, and on desktop (md+).
 */
export function MobileStickyCTA() {
  const location = useLocation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  // Close modal automatically when navigating away
  useEffect(() => {
    setModalOpen(false);
  }, [location.pathname]);
  const path = location.pathname;
  // Determine context
  const isHome = path === '/';
  // Match /:signId — any single segment route that is a real sign id
  const segments = path.split('/').filter(Boolean);
  const signId = segments.length === 1 && !['portal-de-usuario', 'terminos', 'privacidad', 'reembolsos'].includes(segments[0]) ? segments[0] : null;
  const activeSign = signId ? getSignById(signId) : null;
  // Hide on routes that aren't home or a sign subpage
  if (!isHome && !activeSign) return null;
  const buttonColor = activeSign ? activeSign.colors.primary : GOLD;
  const buttonGlow = activeSign ? `${activeSign.colors.primary}80` : 'rgba(201,168,76,0.55)';
  const handleClick = () => {
    if (activeSign) {
      const el = document.getElementById('sign-offers');
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      setModalOpen(true);
    }
  };
  const handleSelectSign = (id: string) => {
    setModalOpen(false);
    // Allow the modal to start closing before navigating
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
      navigate(`/${id}`);
    }, 100);
  };
  return <>
      {/* Sticky bottom CTA — mobile only */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-[90] pointer-events-none" style={{
      paddingBottom: 'max(env(safe-area-inset-bottom), 0px)'
    }} data-id="element-710">
        {/* Soft fade behind the button so it doesn't sit on raw content */}
        <div className="absolute inset-x-0 bottom-0 h-28 pointer-events-none" style={{
        background: 'linear-gradient(180deg, rgba(5,5,8,0) 0%, rgba(5,5,8,0.75) 60%, rgba(5,5,8,0.95) 100%)'
      }} data-id="element-711" />

        <div className="relative px-4 pt-3 pb-3 pointer-events-none" data-id="element-712">
          <motion.button type="button" onClick={handleClick} initial={{
          y: 60,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} whileTap={{
          scale: 0.97
        }} className="pointer-events-auto block w-full rounded-2xl px-6 py-3.5 font-bold tracking-[0.18em] text-[15px] text-center" style={{
          background: activeSign ? `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}DD 100%)` : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
          color: '#050508',
          boxShadow: `0 0 28px ${buttonGlow}, 0 6px 24px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(245,240,225,0.35)`,
          fontFamily: 'Playfair Display, serif'
        }} aria-label="Desbloquear Reino" data-id="element-713">
            <span className="block" data-id="element-714">DESBLOQUEAR REINO</span>
            <span className="block mt-1 text-[10.5px] font-medium tracking-[0.28em] opacity-80" style={{
            color: '#050508'
          }} data-id="element-715">
              ACCESO INMEDIATO
            </span>
          </motion.button>
        </div>
      </div>

      {/* Reinos selector modal — opens only from the landing page */}
      <AnimatePresence data-id="element-716">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="md:hidden fixed inset-0 z-[120] flex items-end justify-center" role="dialog" aria-modal="true" aria-label="Selecciona tu Reino" data-id="element-717">
            {/* Backdrop */}
            <button type="button" aria-label="Cerrar" onClick={() => setModalOpen(false)} className="absolute inset-0 bg-cosmic-black/85 backdrop-blur-sm" data-id="element-718" />

            {/* Sheet */}
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-full max-h-[90vh] overflow-y-auto rounded-t-3xl" style={{
          background: 'linear-gradient(180deg, #0A0A12 0%, #050508 100%)',
          border: `1px solid ${GOLD}33`,
          borderBottom: 'none',
          boxShadow: `0 -10px 60px rgba(201,168,76,0.18), 0 -2px 20px rgba(0,0,0,0.7)`,
          paddingBottom: 'max(env(safe-area-inset-bottom), 16px)'
        }} data-id="element-719">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1" data-id="element-720">
                <div className="h-1 w-12 rounded-full" style={{
              backgroundColor: `${GOLD_LIGHT}55`
            }} data-id="element-721" />
              </div>

              {/* Header */}
              <div className="relative px-5 pt-3 pb-5 text-center" data-id="element-722">
                <button type="button" onClick={() => setModalOpen(false)} aria-label="Cerrar" className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gold-light/80 hover:text-gold-light transition-colors" style={{
              background: 'rgba(232,212,139,0.08)',
              border: `1px solid ${GOLD_LIGHT}33`
            }} data-id="element-723">
                  <XIcon size={18} data-id="element-724" />
                </button>

                <div className="inline-flex items-center gap-2 mb-3" data-id="element-725">
                  <div className="h-px w-8" style={{
                background: `linear-gradient(to right, transparent, ${GOLD})`
              }} data-id="element-726" />
                  <span className="text-[10px] tracking-[0.35em] uppercase font-serif" style={{
                color: GOLD_LIGHT
              }} data-id="element-727">
                    Los 12 Reinos
                  </span>
                  <div className="h-px w-8" style={{
                background: `linear-gradient(to left, transparent, ${GOLD})`
              }} data-id="element-728" />
                </div>

                <h3 className="text-2xl font-serif font-bold text-white mb-1.5 leading-tight" style={{
              textShadow: `0 0 20px ${GOLD}40`
            }} data-id="element-729">
                  Selecciona tu Reino
                </h3>
                <p className="text-[13px] font-serif italic" style={{
              color: 'rgba(232,212,139,0.78)'
            }} data-id="element-730">
                  Elige el que despierta algo dentro de ti.
                </p>
              </div>

              {/* Sign grid */}
              <div className="grid grid-cols-3 gap-2.5 px-4 pb-5" data-id="element-731">
                {signs.map((sign, i) => <motion.button key={sign.id} type="button" onClick={() => handleSelectSign(sign.id)} initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.05 + i * 0.025,
              duration: 0.35,
              ease: 'easeOut'
            }} whileTap={{
              scale: 0.95
            }} className="relative rounded-xl overflow-hidden aspect-[3/4] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-light" style={{
              border: `1px solid ${sign.colors.primary}55`,
              boxShadow: `0 0 14px ${sign.colors.primary}25`
            }} aria-label={`Entrar al Reino de ${sign.name}`} data-id="element-732">
                    <img src={sign.imageUrl} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{
                filter: 'brightness(0.7)'
              }} loading="lazy" data-id="element-733" />
                    <div className="absolute inset-0" style={{
                background: `linear-gradient(180deg, transparent 40%, rgba(5,5,8,0.92) 100%)`
              }} data-id="element-734" />
                    <div className="absolute inset-0" style={{
                background: `radial-gradient(circle at center, ${sign.colors.primary}20 0%, transparent 70%)`
              }} data-id="element-735" />

                    <div className="absolute inset-x-0 bottom-0 p-2 flex flex-col items-center text-center" data-id="element-736">
                      <span className="text-2xl leading-none mb-0.5" style={{
                  color: sign.colors.primary,
                  textShadow: `0 0 10px ${sign.colors.primary}`
                }} data-id="element-737">
                        {sign.symbol}
                      </span>
                      <span className="text-[13px] font-serif font-semibold text-white tracking-wide" style={{
                  textShadow: '0 1px 6px rgba(0,0,0,0.95)'
                }} data-id="element-738">
                        {sign.name}
                      </span>
                    </div>
                  </motion.button>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}